import React from 'react';
import { Handshake, Building, Globe, TrendingUp, Users, Star, CheckCircle, Heart, Target, Award, Rocket, Shield, Brain, Bot } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const PartnershipsPage: React.FC = () => {
  useSEO('partnerships');
  const { isBannerVisible, bannerHeight } = useBanner();
  
  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
            Partnership Opportunities
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
            Join forces with Cognitive Craft Hub to expand your service offerings and unlock new revenue opportunities
          </p>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Whether you're a technology company, consulting firm, or industry specialist, 
            we offer flexible partnership models that help you expand capabilities and serve clients better.
          </p>
        </div>
      </section>

      {/* Partnership Models */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Partnership Models</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Flexible partnership options designed to create mutual value and drive growth
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                <Building className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Technology Partners</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Integrate our AI and blockchain solutions into your existing technology stack 
                to offer enhanced capabilities to your customers.
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />API Integration</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />White-label Solutions</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />Technical Support</li>
              </ul>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Reseller Partners</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Expand your service portfolio by reselling our AI and blockchain solutions 
                to your existing client base with attractive margins.
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />Competitive Pricing</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />Sales Training</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />Marketing Support</li>
              </ul>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                <Globe className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Referral Partners</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Earn attractive commissions by referring clients who need AI or blockchain 
                solutions to our team of experts.
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />Generous Commissions</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />Easy Referral Process</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />Ongoing Support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Partner Benefits</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Unlock powerful advantages and drive mutual growth through strategic partnership
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300 text-center">
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                <TrendingUp className="w-8 h-8 mx-auto" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Revenue Growth</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Unlock new revenue streams with our high-demand AI and blockchain solutions
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300 text-center">
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                <Brain className="w-8 h-8 mx-auto" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Technical Expertise</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Access our team of AI and blockchain experts to support your clients
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300 text-center">
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                <Award className="w-8 h-8 mx-auto" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Market Differentiation</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Stand out from competitors with cutting-edge technology offerings
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300 text-center">
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                <Rocket className="w-8 h-8 mx-auto" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Fast Implementation</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Quick setup and deployment with our proven methodologies and frameworks
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Industries */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Target Industries</h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                Partnership opportunities across key industries driving digital transformation
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center hover:bg-white/80 dark:hover:bg-white/20 transition-all duration-300">
                <Heart className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Healthcare</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  EHR systems, patient analytics, and compliance solutions
                </p>
              </div>
              <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center hover:bg-white/80 dark:hover:bg-white/20 transition-all duration-300">
                <Building className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Finance</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Risk management, fraud detection, and blockchain transactions
                </p>
              </div>
              <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center hover:bg-white/80 dark:hover:bg-white/20 transition-all duration-300">
                <Bot className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Manufacturing</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Predictive maintenance, quality control, and supply chain optimization
                </p>
              </div>
              <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center hover:bg-white/80 dark:hover:bg-white/20 transition-all duration-300">
                <Target className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Retail</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Customer analytics, inventory management, and personalization
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Requirements */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Partnership Requirements</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Building successful partnerships through mutual alignment and shared commitment to excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Ideal Partner Profile</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Established business with proven track record</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Strong client relationships and market presence</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Commitment to quality and customer success</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Alignment with our values and mission</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">What We Provide</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Star className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Comprehensive training and certification</span>
                </li>
                <li className="flex items-start">
                  <Star className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Marketing materials and sales support</span>
                </li>
                <li className="flex items-start">
                  <Star className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Technical documentation and resources</span>
                </li>
                <li className="flex items-start">
                  <Star className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Dedicated partner success manager</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Partner with Us?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Take the first step towards a successful partnership. Contact our partnership team 
              to discuss opportunities and learn how we can grow together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a
                href="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                <Handshake className="w-5 h-5 mr-2" />
                Contact Partnership Team
              </a>
              <a
                href="mailto:team@cognitivecrafthub.com"
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                team@cognitivecrafthub.com
              </a>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-white/20">
              <p className="text-gray-600 dark:text-gray-400">
                Partnership inquiries are typically reviewed within 2-3 business days
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};